import { Link, Navigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import type { ReportPayload } from '../../types'
import { Alert, Card, CardBody, CardHeader, Skeleton } from '../../components/ui'

interface LocatedUser {
  uid?: string
  name?: string
  email?: string
  lat?: number
  lng?: number
  accuracy_m?: number
}

export default function ReportDetail() {
  const { reportId } = useParams<{ reportId: string }>()
  const isLocations = reportId === 'user-locations'

  const reportQuery = useQuery({
    queryKey: ['leader', 'report', reportId],
    queryFn: () => api.get<ReportPayload>(`/leader/reports/${reportId}`),
    enabled: Boolean(reportId) && !isLocations,
  })
  const locationsQuery = useQuery({
    queryKey: ['leader', 'report', 'user-locations'],
    queryFn: () => api.get<{ ok: boolean; items: LocatedUser[] }>('/leader/reports/user-locations'),
    enabled: isLocations,
  })

  if (!reportId) return <Navigate to="/reports" replace />

  if (isLocations) {
    if (locationsQuery.isError) return <Alert tone="danger">{(locationsQuery.error as Error).message}</Alert>
    const people = locationsQuery.data?.items ?? []
    return (
      <>
        <div className="page-head">
          <div><h1>User locations</h1><p className="page-head__sub">Most recent known position per person.</p></div>
        </div>
        <Card>
          <CardBody flush>
            {locationsQuery.isLoading ? (
              <div style={{ padding: 'var(--sp-6)' }}><Skeleton h={20} /></div>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Person</th><th>Coordinates</th><th>Accuracy</th></tr></thead>
                  <tbody>
                    {people.map((p, i) => (
                      <tr key={p.uid ?? i}>
                        <td>{p.name || p.email || p.uid}</td>
                        <td>{p.lat != null ? `${p.lat.toFixed(4)}, ${p.lng?.toFixed(4)}` : '—'}</td>
                        <td>{p.accuracy_m ? `±${Math.round(p.accuracy_m)} m` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      </>
    )
  }

  if (reportQuery.isError) {
    return (
      <Alert tone="danger">
        {(reportQuery.error as Error).message}
        <div style={{ marginTop: 'var(--sp-3)' }}><Link to="/reports">Back to reports</Link></div>
      </Alert>
    )
  }
  if (reportQuery.isLoading || !reportQuery.data) {
    return <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={140} /></div>
  }

  const report = reportQuery.data

  return (
    <>
      <div className="page-head">
        <div><h1>{report.title}</h1></div>
      </div>

      <div className="grid grid--tiles-2" style={{ marginBottom: 'var(--sp-6)' }}>
        {report.kpis.map((k) => (
          <Card key={k.label}>
            <CardBody>
              <div className="stat__label">{k.label}</div>
              <div className="stat__value" style={{ fontSize: 'var(--text-lg)' }}>{k.value}</div>
            </CardBody>
          </Card>
        ))}
      </div>

      {report.breakdowns.map((b) => (
        <Card key={b.title} style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title={b.title} />
          <CardBody flush>
            <div className="table-wrap">
              <table className="table">
                <thead><tr>{b.columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
                <tbody>
                  {b.rows.map((row, i) => (
                    <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      ))}

      <Card>
        <CardHeader title="Detail" />
        <CardBody flush>
          <div className="table-wrap">
            <table className="table">
              <thead><tr>{report.columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
              <tbody>
                {report.rows.map((row, i) => (
                  <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </>
  )
}
